import { useForm } from 'react-hook-form';
import { ApiCustomerIdentity } from '../../../openapi';
import { FormContentIsland, FormTextField, Grid, HookForm, Button } from '@razz-team/ui-web';
import { useCallback, useEffect } from 'react';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import {
  useConfirmPhoneMutation,
  useSetPhoneMutation,
  useUpdatePhoneMutation,
} from '../../../api/identity';
import { useTranslation } from 'react-i18next';
import { OtpField } from '../../Field/OtpField';
import { useFormError } from '../../../hooks/useFormError';

type FormValues = {
  phone: string;
  otp: string;
};

export function UpdatePhoneForm({
  identity,
  onComplete,
}: {
  identity: ApiCustomerIdentity;
  onComplete?: () => void;
}) {
  const { t } = useTranslation();

  const idempotencyKey = useIdempotencyKey();
  const setMutation = useSetPhoneMutation({ idempotencyKey });
  const updateMutation = useUpdatePhoneMutation({ idempotencyKey });
  const confirmMutation = useConfirmPhoneMutation({ idempotencyKey });
  const startMutation = identity.phone ? updateMutation : setMutation;
  const activeMutation = startMutation.isSuccess ? confirmMutation : startMutation;

  useEffect(() => {
    if (activeMutation.isSuccess) {
      onComplete?.();
    }
  }, [activeMutation.isSuccess, onComplete]);

  const form = useForm<FormValues>();

  useFormError(form, 'phone', activeMutation.error);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (startMutation.isSuccess) {
        confirmMutation.mutate({ code: values.otp });
      } else {
        startMutation.mutate({ phone: values.phone });
      }
    },
    [confirmMutation, startMutation],
  );

  return (
    <Grid
      flexDirection="column"
      gap={4}
      component={HookForm}
      form={form}
      onSubmit={handleSubmit}
      size={12}
    >
      <FormContentIsland>
        <FormTextField
          id="phone"
          name="phone"
          label={t('Phone')}
          fullWidth
          required
          inputMode="tel"
          autoComplete="tel"
          disabled={startMutation.isSuccess}
          autoFocus={!startMutation.isSuccess}
        />
        {startMutation.isSuccess ? (
          <OtpField name="otp" label={t('OTP code')} fullWidth required autoFocus />
        ) : null}
      </FormContentIsland>
      <Button type="submit" size="large" loading={activeMutation.isPending} fullWidth>
        {t('Submit')}
      </Button>
    </Grid>
  );
}
